import React from 'react';

import { AccountingDocumentLight } from '@ha/icons';
import { Button, units } from '@hbf/dsl';
import { makeStyles } from 'tss-react/mui';
import { PaymentDialog } from 'ha/modules/PaymentDialog';
import {
  getPricingStructure,
  PricingStructure,
  usePricing,
} from 'ha/modules/Pricing';
import { Listing } from '@ha/api/v2/types/Listing';
import { RentalConditions } from '@ha/api/v2/types/RentalConditions';

const useStyles = makeStyles()(theme => ({
  custom: {
    color: theme.palette.secondary.dark,
  },
  checkIcon: {
    marginRight: theme.spacing(1),
  },
  dialogButtonText: {
    color: theme.palette.secondary.main,
    fontSize: units.rem(1),
  },
  modalButton: {
    paddingLeft: theme.spacing(0),
    textTransform: 'none',
  },
}));

export const PaymentOverviewButton: React.FC<{
  dataTestLocator?: string;
  listing: Listing;
  rentalConditions: RentalConditions | null;
  startDate: string;
  tenantFeeRuleName: string;
  text?: string;
  type?: 'secondaryDark' | 'default';
}> = ({
  text,
  startDate,
  listing,
  rentalConditions,
  tenantFeeRuleName,
  dataTestLocator,
  type = 'default',
}) => {
  const { classes, cx } = useStyles();
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const pricingStructure = getPricingStructure({ rentalConditions });

  const { feeMode } = usePricing({
    listingId: listing.id,
    rentalConditions:
      pricingStructure === PricingStructure.instalment && rentalConditions
        ? rentalConditions
        : {
            isLegacy: true,
            startDate,
            tenantFee: listing.haFee,
            baseTenantFee: listing.baseHaFee,
            currency: listing.currency,
          },
    tenantFeeRuleName,
    pricingStructureOverride: pricingStructure,
  });

  const handleShowPaymentDialog = React.useCallback(async () => {
    setIsDialogOpen(true);
  }, []);

  return (
    <React.Fragment>
      <Button
        onClick={handleShowPaymentDialog}
        className={classes.modalButton}
        data-test-locator={dataTestLocator}
      >
        <AccountingDocumentLight
          fontSize="small"
          className={cx(classes.checkIcon, {
            [classes.custom]: type === 'secondaryDark',
          })}
        />
        <span
          className={cx(classes.dialogButtonText, {
            [classes.custom]: type === 'secondaryDark',
          })}
        >
          {text}
        </span>
      </Button>

      {rentalConditions && (
        <PaymentDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          listing={listing}
          rentalConditions={rentalConditions}
          feeMode={feeMode}
        />
      )}
    </React.Fragment>
  );
};
